import type { Middleware } from '@reduxjs/toolkit';

import { USER_CLEAR_TYPE } from './clearUserPersistAfterMiddleware';

export type ClearZoomChatIdentity = () => void;
export type EndZoomChatEngagement = () => void;
export type RevokeZoomChatIdentity = () => void;

/**
 * Revokes the credential hook, ends the live Zoom engagement and drops the SDK's cached consumer
 * identity whenever the portal session is cleared.
 *
 * Zoom keeps that identity in localStorage, independently of our session, so clearing the portal
 * session does not clear it. `user/clearUser` is dispatched from over twenty places — logout,
 * refresh-token failures, and the deep-link and sign-in-as-patient flows that replace one patient
 * with another — so handling it centrally is what keeps a new patient from inheriting the
 * previous one's chat identity on a shared device.
 *
 * The two halves address opposite ends of the same engagement, which is why neither substitutes for
 * the other. Clearing storage governs what the NEXT patient on this browser inherits. Ending the
 * chat governs what the AGENT is left holding: nothing we do locally — not the sweep, not the
 * eviction reload — reaches Zoom, so without it the engagement stays open in Contact Center with a
 * patient who has signed out, and whatever the agent sends goes into a transcript nobody will read.
 *
 * Shape follows `createClearUserPersistAfterMiddleware`: run after `next(action)` so the reducer has
 * settled, and defer the storage write to a microtask so it never executes during a reducer (Redux
 * error #6, unsubscribe during a synchronous reducer run).
 *
 * The end-chat call is deliberately NOT deferred with it. It is a vendor call, not a storage write,
 * so the reducer hazard does not apply — and the timing does: on the logout path this dispatch is
 * four statements ahead of a hard navigation, and a microtask would put the request into a document
 * that is already unloading. Synchronous here means it goes out while the page is still healthy.
 */
export const createClearZoomChatIdentityMiddleware = (
  clearZoomChatIdentity: ClearZoomChatIdentity,
  endZoomChatEngagement: EndZoomChatEngagement,
  revokeZoomChatIdentity: RevokeZoomChatIdentity
): Middleware => {
  return () => (next) => (action) => {
    const actionType = (action as { type: string })?.type;

    if (actionType === USER_CLEAR_TYPE) {
      // Before the reducer and before React hears about the change, so the credential hook refuses
      // from the instant the session is cleared. The widget that owns the hook only blanks its own
      // session in an effect, which runs after this dispatch returns, and a request Zoom already
      // started would otherwise finish in that gap and hand the signed-out patient's identity to
      // Zoom. Guarded like the steps below so a failure here cannot block the sign-out itself.
      try {
        revokeZoomChatIdentity();
      } catch {
        // The generation moves before anything here can throw, and the later steps still run.
      }
    }

    const result = next(action);

    if (actionType === USER_CLEAR_TYPE) {
      // Guarded separately from the clear below, so a vendor global in an unexpected state cannot
      // cost us the storage sweep — the sweep is the half carrying the disclosure guarantee.
      try {
        endZoomChatEngagement();
      } catch {
        // Best effort. An engagement we fail to end is closed by Contact Center's own inactivity
        // timeout; a sign-out we fail to complete is not recoverable.
      }

      queueMicrotask(() => {
        // Guarded because a throw inside a microtask surfaces as an unhandled exception rather
        // than reaching the dispatching code. Signing out must not be reported as a crash
        // because storage happened to be unavailable.
        try {
          clearZoomChatIdentity();
        } catch {
          // Nothing actionable here: the session is already cleared either way.
        }
      });
    }

    return result;
  };
};
